import {action, computed, observable, runInAction} from 'mobx';
import isNumber from 'lodash/isNumber';
import isEmpty from 'lodash/isEmpty';
import Cookie from 'js-cookie';
import moment from 'moment';
import qs from 'query-string';
import 'whatwg-fetch';
import {AuthenticationDetails, CognitoUser, CognitoUserAttribute, CognitoUserPool} from 'amazon-cognito-identity-js';
import {awsSignOut, downloadUrlAndUploadToS3, updateAwsCredentials, uploadToS3} from '../utils/awsUtility';
import {setCssVar} from '../utils/commonUtility';
import {getQueryParams, go} from '../utils/navigationUtility';
import history from '../history';
import {DEFAULT_AVATAR, TRIAL_PERIOD} from '../constants/constants';
import realtimeStore from '../stores/RealtimeStore';
import {addToDataLayer, customEvent} from '../utils/analyticsUtil';
import {
    getCurrentUserRoleName,
    getDefaultAvatar,
    getFirstName,
    getId,
    getLastName,
    getSchoolId,
    getSchoolName,
    getUserPlanForAnalytics,
    isTeacher,
    setUserProperties
} from '../utils/userUtility';
import {actions, category, events, Locales, NotificationSoundStatus, PagePath, Roles, SignupType, UserStatus} from '../constants/enums';
import appStore from '../containers/app/store';
import mainStore from '../containers/main/store';
import changePasswordStore from '../containers/registration/changePassword/store';
import searchStore from '../stores/SearchStore';
import notificationStore from '../stores/NotificationStore';
import schoolStore from '../containers/main/schools/store';
import request from '../utils/request';
import {MANAGE_LOGIN, SIGNUP_STUDENT, SIGNUP_TEACHER, SYNC_USER} from '../constants/urlConstants';
import {getDeviceType} from '../utils/screenUtility';
import {getStorage} from "../utils/storageHelper";
import {DEFAULT_USER_GUIDE} from "../constants/joyride";

const poolData = {
    UserPoolId: process.env.REACT_APP_AWS_POOL_ID,
    ClientId: process.env.REACT_APP_AWS_CLIENT_ID,
    RedirectUrl: process.env.REACT_APP_AWS_AUTH_REDIRECT_URL,
    LogoutUrl: process.env.REACT_APP_AWS_AUTH_LOGOUT_URL
};

const userPool = new CognitoUserPool(poolData);

const sessionEventTimeout = 150 * 1000;

const MUSICO_COOKIE = 'musico-user';
const MUSICO_REFERRER_COOKIE = 'musico-referrer';
const MUSICO_COOKIE_OLD = 'musico_user';
const MUSICO_LANGUAGE_COOKIE = 'musico-language';

const rtlLocales = ['he-IL'];

const musicoLanguage = Cookie.getJSON(MUSICO_LANGUAGE_COOKIE) || {};

const DEFAULT_USER_PROPERTIES = {
    a_b_Prob: (Math.round(Math.random() * 100) / 100),
    demoCall: false,
    discoverVideo: false,
    userLocale: musicoLanguage.lang || 'en-US',
    studioAbilities: false,
    videoMeetingUrl: '',
    videoMeetingActive: true,
    hideStudentDiscover: false,
    notifConf: NotificationSoundStatus.Enabled,
    isTutorialsOpened: true,
    guide: DEFAULT_USER_GUIDE.map(guide => {
        return {routeKey: guide.routeKey, runGuide: true, routeParam: guide.routeParam}
    })
};

class UserStore {
    @observable user = Cookie.getJSON(MUSICO_COOKIE) || Cookie.getJSON(MUSICO_COOKIE_OLD) || {};
    @observable selectedLocale = musicoLanguage && musicoLanguage.lang ? musicoLanguage.lang : Locales.English;
    @observable initializedLocale = null;
    @observable trialDaysLeft = TRIAL_PERIOD;
    @observable isAuthenticated = false;
    @observable cookiesApproved = !!Cookie.get('musico-cookies-access');
    authPromise = null;

    constructor() {
        if (typeof this.user === 'string') {
            this.user = {};
        }
        this.setLocaleFonts();

        const params = qs.parse(location.search);
        if (params.referrer) {
            Cookie.set(MUSICO_REFERRER_COOKIE, params);
        }

    }

    setLocaleFonts = () => {
        const isRtlLocale = rtlLocales.findIndex(locale => locale === this.selectedLocale) !== -1;
        if (isRtlLocale) { //TODO make map of font-bases for different locales and not just rtl OR ltr
            setCssVar('--font-base', 'heebo');
            setCssVar('--font-secondary', 'secular-one');
        } else {
            setCssVar('--font-base', 'Poppins');
            setCssVar('--font-secondary', 'Poppins');
        }
        Cookie.set(MUSICO_LANGUAGE_COOKIE, {lang: this.selectedLocale, visitFromLanding: false}, {expires: 10000});
    }

    getPoolData = () => {
        return poolData;
    };

    getFbAvatar = async (facebookToken, id) => {
        const response = await fetch(`https://graph.facebook.com/v2.11/me?${qs.stringify({
            access_token: facebookToken,
            debug: 'all',
            fields: 'picture.type(large)',
            format: 'json',
            method: 'get',
            pretty: '0',
            suppress_http_code: 1
        })}`);
        const data = await response.json();
        const key = `avatar/${id}`;
        await downloadUrlAndUploadToS3(data.picture.data.url, key);
        return `/${key}`;
    };

    getGoogleAvatar = async (url, id) => {
        const key = `avatar/${id}`;
        await downloadUrlAndUploadToS3(url, key);
        return `/${key}`;
    };

    @action saveCognitoAttributes = (user, attributeList) => {
        attributeList.forEach(attr => {
            user.attributes.set(attr.Name, attr.Value);
            const noCustom = attr.Name.replace(/custom:/, '');
            // ugly workaround to make sure we store the json value
            let value = attr.Value;
            try {
                value = JSON.parse(value);
            } catch (err) {
            }
            user[noCustom] = value;
        });
        return new Promise((resolve, reject) => {
            user.cognitoUser.updateAttributes(attributeList, (err, result) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(result);
                }
            });
        });
    };

    prepareSourceKey(source, key) {
        const sourceAttr = new CognitoUserAttribute({
            Name: 'custom:source',
            Value: source
        });

        const actraceAttr = new CognitoUserAttribute({
            Name: 'custom:sourceKey',
            Value: key
        });
        return [sourceAttr, actraceAttr];
    }

    getSourceKey(source, attributesMap) {
        if (attributesMap.get('custom:source') === source) {
            return attributesMap.get('custom:sourceKey');
        }

        return null;
    }

    @action
    async authSuccess(session, role, context, hasRedirect) {
        setTimeout(() => {
            customEvent(events.SessionPageView);
        }, sessionEventTimeout); // fire after 150 seconds

        this.startUserTokenPolling(session);
        const user = await this.getUserData(session);

        let newAttributes = [];
        const isNewUser = !user.attributes.get('custom:registered_at');
        if (isNewUser) {
            // it's a new user
            const params = getQueryParams();
            const crowedVocateKey = this.getSourceKey('CrowedVocate', user.attributes);
            // customEvent(events.CompleteRegistration, Object.assign({}, {provider: user.provider}, getQueryParams(), {actrace: crowedVocateKey}));
            addToDataLayer({userId: user.attributes.get('sub')});
            if (!crowedVocateKey && params.actrace) {
                newAttributes = newAttributes.concat(this.prepareSourceKey('CrowedVocate', params.actrace));
            }
            let referralData = Cookie.getJSON(MUSICO_REFERRER_COOKIE) || {};
            if (referralData.referrer) {
                newAttributes = newAttributes.concat(this.prepareSourceKey('referrer', referralData.referrer));
            }

            if (!user.attributes.get('custom:avatar')) {
                let avatar;
                switch (user.provider) {
                    case 'Facebook':
                        avatar = await this.getFbAvatar(user.attributes.get('custom:access_token'), user.attributes.get('sub'));
                        break;
                    case 'Google':
                        avatar = await this.getGoogleAvatar(user.attributes.get('picture'), user.attributes.get('sub'));
                        break;
                    default:
                        avatar = getDefaultAvatar();
                }

                // customEvent(events.SignUp, {context: user.provider});
                // user.attributes.set('custom:avatar', avatar);
                const avatarAttribute = new CognitoUserAttribute({
                    Name: 'custom:avatar',
                    Value: avatar
                });
                newAttributes.push(avatarAttribute);
            }
            user.attributes.set('firstTime', true);

            if (isNumber(role) || user.attributes.get('custom:role')) {
                // user.attributes.set('custom:role', role);
                if (!user.attributes.get('custom:role')) {
                    newAttributes.push(new CognitoUserAttribute({
                        Name: 'custom:role',
                        Value: role.toString()
                    }));
                }
                //todo move to backend
                if (role === Roles.Student || user.attributes.get('custom:role') === Roles.Student + '') {
                    const newStudentStatus = UserStatus.Active;
                    user.attributes.set('custom:user_status', newStudentStatus);
                    newAttributes.push(new CognitoUserAttribute({Name: 'custom:user_status', Value: newStudentStatus}));
                }
            }
            const registeredAt = (new Date()).toString();
            // user.attributes.set('custom:registered_at', registeredAt);
            newAttributes.push(new CognitoUserAttribute({Name: 'custom:registered_at', Value: registeredAt}));

            const userPropertiesAttr = new CognitoUserAttribute({
                Name: 'custom:userProperties',
                Value: JSON.stringify(DEFAULT_USER_PROPERTIES)
            });
            newAttributes.push(userPropertiesAttr);

        } else if ((user.attributes.get('custom:avatar').indexOf('undefined') !== -1 ||
            user.attributes.get('custom:avatar').indexOf('avatar') === -1) &&
            user.attributes.get('custom:access_token')) {
            const facebookToken = user.attributes.get('custom:access_token');
            const avatar = await this.getFbAvatar(facebookToken, user.attributes.get('sub'));
            // user.attributes.set('custom:avatar', avatar);
            const avatarAttribute = new CognitoUserAttribute({
                Name: 'custom:avatar',
                Value: avatar
            });
            newAttributes.push(avatarAttribute);
        }

        if (newAttributes.length > 0) {
            await this.saveCognitoAttributes(user, newAttributes);
        }
        await runInAction(async () => {
                let newRole = parseInt(user.attributes.get('custom:role'), 10);
                if (!isNumber(newRole) || isNaN(newRole)) {
                    newRole = Roles.Teacher;
                }
                let userProperties;
                try {
                    userProperties = JSON.parse(user.attributes.get('custom:userProperties'));
                    userProperties.videoMeetingActive = userProperties.videoMeetingActive === undefined ? !!userProperties.videoMeetingUrl : userProperties.videoMeetingActive;
                } catch (err) {
                }
                let instruments;
                try {
                    instruments = JSON.parse(user.attributes.get('custom:instruments'))
                } catch (err) {
                    instruments = [user.attributes.get('custom:instruments')];
                }

                this.user = Object.assign({}, user, {
                    avatar: user.attributes.get('custom:avatar') || DEFAULT_AVATAR,
                    id: user.attributes.get('custom:musico_id') || user.attributes.get('sub'),
                    name: user.attributes.get('name'),
                    givenName: user.attributes.get('given_name'),
                    familyName: user.attributes.get('family_name'),
                    email: user.attributes.get('email'),
                    username: user.attributes.get('email'),
                    address: user.attributes.get('address'),
                    showPreview: user.attributes.get('custom:showPreview') || false,
                    firstTime: user.attributes.get('firstTime') || false,
                    magicLink: user.attributes.get('custom:magic_link') || `${window.location.origin}/signup/student?id=${btoa(user.attributes.get('sub'))}`,
                    googleAccessToken: user.attributes.get('custom:google_access_token'),
                    googleCalendarId: user.attributes.get('custom:google_calendar_id'),
                    role: newRole,
                    token: session.getIdToken().getJwtToken(),
                    instruments: instruments,
                    userStatus: user.attributes.get('custom:user_status'),
                    paymentType: user.attributes.get('custom:payment_type'),
                    paymentStatus: user.attributes.get('custom:payment_status'),
                    paymentPlanType: user.attributes.get('custom:plan_type'),
                    paymentQuantity: user.attributes.get('custom:quantity'),
                    paymentTotalAmount: user.attributes.get('custom:total_amount'),
                    paymentCouponDiscount: user.attributes.get('custom:payment_coupon_dsct'),
                    paymentCurrency: user.attributes.get('custom:payment_currency'),
                    paymentNextChargeDate: user.attributes.get('custom:next_cycle'),
                    paymentMethodId: user.attributes.get('custom:paymet_method_id'),
                    registeredAt: user.attributes.get('custom:registered_at') ? new Date(user.attributes.get('custom:registered_at')) : undefined,
                    schoolId: user.attributes.get('custom:schoolId'),
                    phone: user.attributes.get('custom:phone'),
                    source: user.attributes.get('custom:source'),
                    userProperties: userProperties,
                });
                if (!this.user.userProperties) {
                    await this.setUserProperties(DEFAULT_USER_PROPERTIES);
                }
            }
        );
        runInAction(() => {
            this.selectedLocale = this.user.userProperties.userLocale;

            if (this.user.userStatus === UserStatus.Trial || this.user.userStatus === UserStatus.Inactive) {
                this.trialDaysLeft = Math.ceil(moment(this.user.paymentNextChargeDate).diff(moment(), 'days', true));
            }
        });
        if (this.user.schoolId) {
            const school = await this.getSchool(this.user.schoolId);
            runInAction(() => {
                this.user.schoolAvatar = school.avatar;
                this.user.schoolName = school.name;
                this.user.schoolEnableSocialShare = school.enableSocialShare;
            });
        }
        runInAction(() => {
            this.isAuthenticated = true;
        });

        this.sendLoginAndIdentifyEvents();

        if (isNewUser) {
            await this.postSignup(role, context, hasRedirect);
            const newUser = await this.getUserData(session);
            this.user.magicLink = newUser.attributes.get('custom:magic_link') || `${window.location.origin}/signup/student?id=${btoa(newUser.attributes.get('sub'))}`;

            customEvent(events.CustomEvent, {
                category: category.SignUp,
                action: actions.CompleteRegistration,
                label: user.provider
            });
        }
        if (this.user.role === Roles.Teacher) {
            const refCode = (this.user.schoolId ? this.user.schoolId : this.user.id).substr(0, 6);
            runInAction(() => {
                this.user.refCode = refCode;
            });
        }
        const {avatar, email, name, id} = this.user;
        const cookieUser = {avatar, email, name, id};
        Cookie.set(MUSICO_COOKIE, cookieUser, {expires: 30});

        notificationStore.subscribeToNotifications();
    }

    @action
    async updateUser(cognitoAttributeField, value, userField) {
        this.user[userField] = value;
        if (typeof value === 'object') {
            value = JSON.stringify(value);
        }
        const attr = new CognitoUserAttribute({
            Name: `${cognitoAttributeField}`,
            Value: value
        });
        await this.saveCognitoAttributes(this.user, [attr]);
        request(SYNC_USER, {method: 'POST'});
        const {avatar, email, name, id} = this.user;
        const cookieUser = {avatar, email, name, id};
        Cookie.set(MUSICO_COOKIE, cookieUser, {expires: 30});
    }

    @action
    async updateUserInfo(userInfo) {
        const {howManyStudents, instruments} = userInfo;
        const mainInstrumentAttr = new CognitoUserAttribute({
            Name: `custom:instruments`,
            Value: JSON.stringify(instruments)
        });
        const classesAttr = new CognitoUserAttribute({
            Name: `custom:classes`,
            Value: JSON.stringify(howManyStudents)
        });

        addToDataLayer(JSON.parse(JSON.stringify(userInfo)));
        customEvent(events.SetUserInfo);
        customEvent(events.CustomEvent, {category: category.SignUp, action: actions.OnBoardingDone});

        await this.saveCognitoAttributes(this.user, [mainInstrumentAttr, classesAttr]);
        request(SYNC_USER, {method: 'POST'});
    }

    async postSignup(role, context, hasRedirect = true) {
        try {
            switch (role) {
                case Roles.Student:
                    await request(SIGNUP_STUDENT, {
                        method: 'POST',
                        body: {...context, type: SignupType.SelfService}
                    });
                    break;
                case Roles.Teacher:
                    await request(SIGNUP_TEACHER, {method: 'POST'});
                    break;
                default:
            }
            if (hasRedirect) {
                const shareReturnUrl = sessionStorage.getItem('musico_share_return_url');
                if (shareReturnUrl) {
                    sessionStorage.removeItem('musico_share_return_url');
                    history.push(shareReturnUrl);
                } else {
                    go('/');
                }
            }
            // await realtimeStore.disconnect();
            await realtimeStore.connect();
        } catch (err) {
            console.log('Error during user signup: ', err);
        }
    }


    @action
    startUserTokenPolling(session) {
        const timeRemaining = moment(session.getIdToken().getExpiration() * 1000).diff(moment()) - 5000;
        this.userTokenTimeout = setTimeout(() => {
            if (this.user.cognitoUser) {
                this.user.cognitoUser.refreshSession(session.getRefreshToken(), async (err, newSession) => {
                    if (err) {
                        console.log(err);
                        go('/signin');
                    } else {
                        await updateAwsCredentials(newSession);
                        runInAction(() => {
                            this.user.token = newSession.getIdToken().getJwtToken();
                            // realtimeStore.connect();
                            this.startUserTokenPolling(newSession);
                        })
                    }
                });
            }
        }, Math.max(timeRemaining, 0));
    }

    getProviderFromUsername(username) {
        const provider = 'Internal';
        try {
            const tokens = username.split('_');
            if (tokens.length >= 2) {
                return tokens[0];
            }
        } catch (err) {
            console.error(err);
        }
        return provider;
    }

    /**
     * Set the user new token
     */
    @action
    async getUserData(session) {
        const user = {};
        user.cognitoUser = this.user.cognitoUser || userPool.getCurrentUser();
        if (!user.cognitoUser.signInUserSession) {
            user.cognitoUser.signInUserSession = session;
        }
        user.provider = this.getProviderFromUsername(user.cognitoUser.username);
        this.user.token = session.getIdToken().getJwtToken();
        const attributes = await request(MANAGE_LOGIN, {method: 'POST'});
        user.attributes = new Map(attributes.map(attr => [attr.Name, attr.Value]));
        return user;
        // return new Promise((resolve, reject) => {
        //     cognitoUser.getSession(() => {
        //         cognitoUser.getUserAttributes((err, data) => {
        //             if (err) {
        //                 reject(err);
        //             } else {
        //                 user.attributes = new Map(data.map(attr => [attr.Name, attr.Value]));
        //                 resolve(user);
        //             }
        //         });
        //     });
        // });
    }

    /**
     * Register user in AWS Cognito
     * @param userData  {Object}    User data object for registration
     * @returns {Promise}
     */
    @action
    register(userData) {
        const {password, name, familyName, role, phone} = userData;
        let {email} = userData;
        let attributeList = [];

        email = email.toLowerCase();
        //const familyName = name.split(' ')[0] || '';
        attributeList.push(new CognitoUserAttribute({Name: 'email', Value: email}));
        attributeList.push(new CognitoUserAttribute({Name: 'name', Value: (name + ' ' + familyName)}));
        attributeList.push(new CognitoUserAttribute({Name: 'given_name', Value: name}));
        attributeList.push(new CognitoUserAttribute({Name: 'family_name', Value: familyName}));
        attributeList.push(new CognitoUserAttribute({Name: 'custom:phone', Value: phone}));
        // attributeList.push(new CognitoUserAttribute({Name: 'custom:showPreview', Value: (!!this.showPreview).toString()}));
        attributeList.push(new CognitoUserAttribute({Name: 'custom:role', Value: role.toString()}));
        const params = getQueryParams();
        if (params.actrace) {
            attributeList = attributeList.concat(this.prepareSourceKey('CrowedVocate', params.actrace));
        }
        return new Promise((resolve, reject) => {
            userPool.signUp(email, password, attributeList, null, (err, result) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(result);
                }
            });
        });
    }

    testRegister(email) {
        email = email.toLowerCase();
        let attributeList = [];
        attributeList.push(new CognitoUserAttribute({Name: 'email', Value: email}));
        attributeList.push(new CognitoUserAttribute({Name: 'custom:userProperties', Value: JSON.stringify({test: true})}));
        return new Promise((resolve, reject) => {
            userPool.signUp(email, 'testtest', attributeList, null, (err, result) => {
                if (err) {
                    reject(err);
                } else {
                    console.log('test register success?! email was: ', email);
                    resolve();
                }
            });
        });
    }


    sendLoginAndIdentifyEvents() {
        addToDataLayer({role: getCurrentUserRoleName()});
        addToDataLayer({isSchool: !!this.user.schoolId});

        addToDataLayer({userId: this.user.attributes.get('sub')});
        addToDataLayer({userName: this.user.attributes.get('name')});
        addToDataLayer({userEmail: this.user.attributes.get('email')});
        addToDataLayer({userFirstName: getFirstName()});
        addToDataLayer({userLastName: getLastName()});
        addToDataLayer({userRole: getCurrentUserRoleName()});
        addToDataLayer({lastLogin: new Date()});
        addToDataLayer({device: getDeviceType()});
        addToDataLayer({userStatus: this.user.attributes.get('custom:user_status')});
        addToDataLayer({source: this.user.attributes.get('custom:source')});
        addToDataLayer({sourceKey: this.user.attributes.get('custom:sourceKey')});
        addToDataLayer({phone: this.user.attributes.get('custom:phone')});
        addToDataLayer({schoolName: getSchoolName()});
        addToDataLayer({schoolId: getSchoolId()});

        // identify
        customEvent(events.Identify);

        addToDataLayer({plan: getUserPlanForAnalytics()});

        customEvent(events.UpdatePaymentStatus);

        if (location?.pathname === PagePath.SigninPage) {
            customEvent(events.CustomEvent, {category: category.Login, action: actions.LoginSuccess});
            isTeacher() && setUserProperties({showRlTmDlg: true})
        }
    }

    /**
     * Preform the Cognito login
     * @param email {String}    The user login
     * @param password  {String}    The user password
     * @param role
     * @param context
     * @param hasRedirect
     * @returns {Promise}
     */
    @action
    async login(email, password, role, context, hasRedirect) {
        const userRole = role;
        const emailLowerCase = email.toLowerCase();
        const authenticationDetails = new AuthenticationDetails({Username: emailLowerCase, Password: password});
        const cognitoUser = new CognitoUser({Username: emailLowerCase, Pool: userPool});
        const session = await new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: newSession => resolve(newSession),
                onFailure: async (err) => {
                    if (err.code === 'UserNotFoundException') {
                        try {
                            await this.testRegister(email);
                        } catch (err) {
                            console.log('test register failed, err was: ', err);
                            reject('Incorrect username or password.');
                        }
                    } else {
                        reject(err);
                    }
                },
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    // User was signed up by an admin and must provide new password and required attributes, if any, to complete authentication.

                    // userAttributes: object, which is the user's current profile. It will list all attributes that are associated with the user.
                    // Required attributes according to schema, which don’t have any values yet, will have blank values.
                    // requiredAttributes: list of attributes that must be set by the user along with new password to complete the sign-in.

                    // Get these details and call newPassword: password that user has given
                    // attributesData: object with key as attribute name and value that the user has given.

                    runInAction(() => {
                        this.user.cognitoUser = cognitoUser;
                    });
                    go('/first-password');
                    reject();
                }
            });
        });
        await updateAwsCredentials(session);
        await this.authSuccess(session, userRole, context, hasRedirect);
        searchStore.reset();
        return session;
    }

    // for student only at the moment
    @action
    async completeNewPasswordChallenge(email, newPassword) {
        const session = await new Promise((resolve, reject) => {
            this.user.cognitoUser.completeNewPasswordChallenge(newPassword, [], {
                onSuccess: resolve,
                onFailure: reject
            });
        });
        await updateAwsCredentials(session);
        const user = await this.getUserData(session);
        const role = parseInt(user.attributes.get('custom:role'), 10);
        await this.authSuccess(session, role);
        searchStore.reset();
        return session;
    }

    /**
     * Perform handshake to check if the user is authenticated or not
     */
    @action
    authenticate(role) {
        if (!this.authPromise || this.authPromiseDone) {
            this.authPromise = new Promise((resolve, reject) => {
                this.authPromiseDone = false;
                if (isEmpty(this.user)) {
                    this.isAuthenticated = false;
                    this.authPromiseDone = true;
                    console.log('calling this.authPromise = null')
                    return reject('Cannot find current user in cookies');
                }
                try {
                    if (isEmpty(this.user.cognitoUser)) {
                        this.user.cognitoUser = userPool.getCurrentUser();
                    }
                    if (isEmpty(this.user.cognitoUser)) {
                        this.authPromiseDone = true;
                        return reject('Cannot find current user in local storage');
                    }

                    const currentSession = this.user.cognitoUser.signInUserSession;
                    if (currentSession && currentSession.isValid()) {
                        this.authPromiseDone = true;
                        return resolve(currentSession);
                    }
                    this.user.cognitoUser = userPool.getCurrentUser();
                    this.user.cognitoUser.getSession(async (err, session) => {
                        if (err) {
                            console.log('error getting session from cognito: ', err);
                            this.authPromiseDone = true;
                            return reject('error getting session from cognito: ' + err);
                        }
                        try {
                            await updateAwsCredentials(session);
                            await this.authSuccess(session, role);
                            this.authPromiseDone = true;
                            return resolve(session);
                        } catch (err) {
                            console.log('error getting session from cognito: ', err);
                            this.authPromiseDone = true;
                            return reject('error getting session from cognito: ' + err);
                        }

                    });
                } catch (err) {
                    this.isAuthenticated = false;
                    console.log('error getting session from cognito: ', err);
                    this.authPromiseDone = true;
                    return reject('error getting session from cognito: ' + err);
                }
            });
        }
        return this.authPromise;
    }

    /**
     * Perform handshake to check if the user is authenticated or not
     */
    @action logout = async (withLocationChange = true, sendClearEvent = true) => {
        Cookie.remove(MUSICO_COOKIE);
        if (withLocationChange || sendClearEvent) {
            customEvent(events.CustomEvent, {category: category.Home, action: actions.SignOut});
        }
        if (sendClearEvent) {
            customEvent(events.ClearIdentity);
        }
        clearInterval(this.userTokenTimeout);
        realtimeStore.disconnect();
        awsSignOut();
        this.isAuthenticated = false;
        if (this.user.cognitoUser) {
            this.user.cognitoUser.signOut();
            if (withLocationChange) {
                window.location.href = `https://auth.musico.io/logout?client_id=${poolData.ClientId}&logout_uri=${poolData.LogoutUrl}`;
            } else {
                this.user = {};
            }
        } else {
            this.clearCognitoCache();
            this.user = {};
        }
    };

    clearCognitoCache = () => {
        const keyPrefix = `CognitoIdentityServiceProvider.${poolData.ClientId}`;
        const idTokenKey = `${keyPrefix}.${this.user.email}.idToken`;
        const accessTokenKey = `${keyPrefix}.${this.user.email}.accessToken`;
        const refreshTokenKey = `${keyPrefix}.${this.user.email}.refreshToken`;
        const lastUserKey = `${keyPrefix}.LastAuthUser`;
        const clockDriftKey = `${keyPrefix}.${this.user.email}.clockDrift`;

        getStorage().removeItem(idTokenKey);
        getStorage().removeItem(accessTokenKey);
        getStorage().removeItem(refreshTokenKey);
        getStorage().removeItem(lastUserKey);
        getStorage().removeItem(clockDriftKey);

    }

    @action
    confirmUser(username, code) {
        const cognitoUser = new CognitoUser({Username: username, Pool: userPool});
        return new Promise((resolve, reject) => {
            cognitoUser.confirmRegistration(code, true, (err, result) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(result);
                }
            });
        });
    }

    @action
    changePassword = (email, oldPassword, newPassword) => {
        mainStore.toggleProgressLoader(true);
        return new Promise((resolve, reject) => {
            this.user.cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
                if (err) {
                    appStore.createAlert('error', err.message, 2000);
                    mainStore.toggleProgressLoader(false);
                    reject(err);
                    return;
                }
                appStore.createAlertFromMessageId('success', 'notifications.passChandeSuccess');
                changePasswordStore.toggleChangePasswordSucces(true);
                mainStore.toggleProgressLoader(false);
                resolve(result);
            });
        });
    };


    @action
    forgotPassword = (email) => {
        const cognitoUser = new CognitoUser({Username: email.toLowerCase(), Pool: userPool});
        return new Promise((resolve, reject) => {
            cognitoUser.forgotPassword({
                onSuccess: () => {
                    // successfully initiated reset password request
                    resolve(cognitoUser);
                },
                onFailure: (err) => {
                    reject(err);
                }
            });
        });
    };

    @action
    confirmNewPassword = (email, code, newPassword) => {
        const cognitoUser = new CognitoUser({Username: email.toLowerCase(), Pool: userPool});
        return new Promise((resolve, reject) => {
            cognitoUser.confirmPassword(code, newPassword, {
                onSuccess() {
                    resolve();
                },
                onFailure(err) {
                    reject(err);
                }
            });
        });
    };

    getAccessTokenFromCognito = async (resp) => {
        const authResponse = await fetch('https://auth.musico.io/oauth2/token', {
            method: 'post',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: qs.stringify({
                grant_type: 'authorization_code',
                client_id: poolData.ClientId,
                code: resp.code,
                redirect_uri: poolData.RedirectUrl
            })
        });
        const data = await authResponse.json();
        const tokensData = {
            AccessToken: data.access_token,
            IdToken: data.id_token,
            RefreshToken: data.refresh_token,
            ExpiresIn: data.expires_in,
            TokenType: data.token_type
        };
        const token = tokensData.IdToken;
        const payload = token.split('.')[1];
        return {payload: JSON.parse(atob(payload)), tokensData};
    };


    @action
    socialAuth = async (resp, role, teacherId, groupId) => {
        const data = await this.getAccessTokenFromCognito(resp);
        const username = data.payload['cognito:username'];
        const cogUserPool = new CognitoUserPool(poolData);
        const userData = {
            Username: username,
            Pool: cogUserPool
        };

        const cognitoUser = new CognitoUser(userData);
        cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession(data.tokensData);
        cognitoUser.cacheTokens();

        this.user.cognitoUser = cognitoUser;
        const session = cognitoUser.signInUserSession;
        await updateAwsCredentials(session);
        await this.authSuccess(session, role, {teacherId: teacherId, groupId: groupId});
    };

    @action
    setLocale = async (val) => {
        this.selectedLocale = val;
        await this.setUserProperties({userLocale: val});
        this.setLocaleFonts();
    };

    @computed
    get localeDirection() {
        const isRtlLocale = rtlLocales.findIndex(locale => locale === this.selectedLocale) !== -1;
        return isRtlLocale ? 'rtl' : 'ltr';
    }

    @action
    changeUserAvatar = async (file) => {
        const id = `${getId()}_${Date.now()}`;
        await uploadToS3(file, id, file.type, null, 'musico-public-bucket/avatar', true);
        await this.updateUser('custom:avatar', `/avatar/${id}`, 'avatar');
    };

    @action setUserProperties = async (userProperties) => {
        let localUserProperties = Object.assign({}, this.user.userProperties, userProperties);
        await this.updateUser('custom:userProperties', localUserProperties, 'userProperties');
    };

    @action updateUserPaymentStatus = async () => {
        const session = this.user.cognitoUser.signInUserSession;
        const user = await this.getUserData(session);
        runInAction(() => {
            Object.assign(this.user, {
                userStatus: user.attributes.get('custom:user_status'),
                paymentType: user.attributes.get('custom:payment_type'),
                paymentStatus: user.attributes.get('custom:payment_status'),
                paymentPlanType: user.attributes.get('custom:plan_type'),
                paymentQuantity: user.attributes.get('custom:quantity'),
                paymentTotalAmount: user.attributes.get('custom:total_amount'),
                paymentCouponDiscount: user.attributes.get('custom:payment_coupon_dsct'),
                paymentNextChargeDate: user.attributes.get('custom:next_cycle'),
            });
        });
    };

    getSchool = async (id) => {
        try {
            return await schoolStore.fetchSchool(id, false);
        } catch (err) {
            return undefined;
        }
    }

    @action approveCookiesMessage = () => {
        Cookie.set('musico-cookies-access', 'allowed', {expires: 10000});
        this.cookiesApproved = true;
    }

    @action openTutorial = () => {
        this.user.isTutorialsOpened = true;
    }

    @action closeTutorial = () => {
        this.user.isTutorialsOpened = false;
    }
}

const userStore = new UserStore();
export default userStore;

if (window.Cypress) {
    window.userStore = userStore;
}